import React, {
  useState,
  useEffect,
  useRef,
  ReactNode,
  RefObject,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  alignment?: "left" | "right" | "center";
}

const DropdownMenu = ({
  ref,
  isOpen,
  x,
  y,
  isClosing,
  onClose,
  children,
}: {
  ref: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  x: number;
  y: number;
  isClosing: boolean;
  onClose: () => void;
  children: ReactNode;
}) => {
  return (
    <div
      ref={ref}
      id="dropdown-menu"
      className={`fixed w-48 max-w-64 max-h-48 sm:max-h-64 bg-white rounded-md shadow-lg border border-gray-200 z-[100] overflow-auto ${isClosing ? "animate-dropdown-close" : "animate-dropdown-open"}`}
      style={{ left: `${x}px`, top: `${y}px` }}
      onClick={onClose}
    >
      <div className="py-1 max-h-80">{children}</div>
    </div>
  );
};

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  children,
  onOpenChange,
  alignment = "right",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [ignoreScroll, setIgnoreScroll] = useState(false);

  const closeDropdown = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      onOpenChange?.(false);
    }, 150); // Match animation duration
    setIgnoreScroll(false);
  }, [onOpenChange]);

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      setIsOpen(true);
      onOpenChange?.(true);
    }
  }, [closeDropdown, isOpen, onOpenChange]);

  const [x, setX] = useState(0);
  const [y, setY] = useState(0);

  const calculatePosition = useCallback(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();

      const tmpY = Math.round(triggerRect.bottom);
      setY(tmpY);
      if (alignment === "left") {
        const tmpX = Math.round(triggerRect.left - menuRect.width);
        setX(tmpX);
      } else if (alignment === "right") {
        const tmpX = triggerRect.right;
        setX(tmpX);
      } else {
        const tmpX = Math.round(
          triggerRect.right - menuRect.width / 2 - triggerRect.width / 2,
        );
        setX(tmpX);
      }
    }
  }, [alignment, menuRef, triggerRef, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      if (isOpen) {
        closeDropdown();
      }
    };

    const handleScrollOutside = (event: Event) => {
      if (ignoreScroll) {
        // calculatePosition();
        return;
      }

      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      if (isOpen) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScrollOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScrollOutside);
    };
  }, [closeDropdown, isOpen, ignoreScroll, calculatePosition]);

  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  const handleDropDownClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as Node;

    const inputElement = document.getElementById("search");

    if (inputElement && inputElement.contains(target)) {
      // Prevent clicking off the dropdown
      inputElement.click();
      setIgnoreScroll(true);
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onClickCapture={handleDropDownClick}
    >
      <div ref={triggerRef} onClick={toggleDropdown}>
        {trigger}
      </div>

      {isOpen &&
        createPortal(
          <DropdownMenu
            ref={menuRef}
            isOpen={isOpen}
            x={x}
            y={y}
            isClosing={isClosing}
            onClose={closeDropdown}
            children={children}
          />,
          document.getElementById("dialog")?.parentElement ?? document.body,
        )}
    </div>
  );
};

export { Dropdown };
